import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { eur } from "@/lib/api";
import { useBrand } from "@/context/BrandContext";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, Loader2, ChefHat } from "lucide-react";

export default function MenuExport() {
  const navigate = useNavigate();
  const { brand } = useBrand();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/recipes")
      .then((r) => setRecipes(r.data))
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    recipes.forEach((r) => {
      const cat = r.category?.trim() || "Senza categoria";
      (g[cat] = g[cat] || []).push(r);
    });
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [recipes]);

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );

  return (
    <div className="min-h-screen bg-secondary/40 py-8">
      <div className="max-w-3xl mx-auto px-4">
        <div className="no-print flex justify-between mb-4">
          <Button variant="ghost" onClick={() => navigate("/ricette")} className="gap-2" data-testid="menu-back-btn">
            <ArrowLeft className="w-4 h-4" /> Torna alle ricette
          </Button>
          <Button onClick={() => window.print()} className="rounded-full gap-2" data-testid="print-menu-btn">
            <Printer className="w-4 h-4" /> Stampa / Salva PDF
          </Button>
        </div>

        <div className="print-area bg-white text-black rounded-lg shadow-sm p-10" data-testid="menu-export">
          <div className="text-center border-b-2 border-black pb-5 mb-6">
            <div className="w-14 h-14 rounded-lg bg-black flex items-center justify-center mx-auto mb-3 overflow-hidden">
              {brand.logo ? (
                <img src={brand.logo} alt="logo" className="w-full h-full object-contain" />
              ) : (
                <ChefHat className="w-6 h-6 text-white" />
              )}
            </div>
            <h1 className="font-display text-4xl font-extrabold tracking-tight uppercase">{brand.business_name}</h1>
            <p className="text-[11px] uppercase tracking-[0.25em] text-gray-500 mt-1">Listino · prezzi e allergeni</p>
          </div>

          {recipes.length === 0 ? (
            <p className="text-center text-gray-500 py-10">Nessuna ricetta da mostrare.</p>
          ) : (
            grouped.map(([cat, list]) => {
              const fcVals = list.filter((r) => r.food_cost_pct).map((r) => r.food_cost_pct);
              const avgFc = fcVals.length ? (fcVals.reduce((a, b) => a + b, 0) / fcVals.length).toFixed(1) : "—";
              return (
                <div key={cat} className="mb-8" data-testid={`menu-cat-${cat}`}>
                  <div className="flex items-baseline justify-between border-b border-black pb-1 mb-3">
                    <h2 className="font-display font-bold text-xl uppercase tracking-wide">{cat}</h2>
                    <span className="text-[11px] text-gray-500">
                      {list.length} piatti · food cost medio {avgFc}%
                    </span>
                  </div>
                  <div className="space-y-3">
                    {list.map((r) => (
                      <div key={r.id} className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-baseline gap-2">
                            <span className="font-semibold">{r.name}</span>
                            <span className="flex-1 border-b border-dotted border-gray-400 translate-y-[-3px]" />
                            <span className="font-mono font-bold">{eur(r.selling_price_gross)}</span>
                          </div>
                          {r.allergens?.length > 0 && (
                            <p className="text-[10px] uppercase tracking-wide text-gray-500 mt-0.5">
                              Allergeni: {r.allergens.join(", ")}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}

          <p className="text-[10px] text-gray-400 mt-8 border-t border-black/20 pt-3 text-center uppercase tracking-wider">
            {brand.business_name} · {new Date().toLocaleDateString("it-IT")}
          </p>
        </div>
      </div>
    </div>
  );
}
